import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Form, message, Divider, Modal, Table, Checkbox, Switch } from 'antd';
import { LayoutOutlined, SaveOutlined, SendOutlined, CheckSquareOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../api/axios';
import { DEVICE_TYPES } from '../utils/deviceTypes';

const SchoolPageConfigPage = ({ teacher }) => {
  const [schoolPageConfig, setSchoolPageConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deviceModalVisible, setDeviceModalVisible] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const styleOptions = [
    { value: 'natural', label: '竖屏自然风' }
  ];

  const gradientTypeOptions = [
    { value: 'horizontal', label: '左右' },
    { value: 'vertical', label: '上下' },
    { value: 'radial', label: '内外' }
  ];

  const themeColors = [
    { name: '深蓝渐变', bgGradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', textColor: '#ffffff', borderColor: '#4a9eff' },
    { name: '绿色渐变', bgGradient: 'linear-gradient(135deg, #0f3460 0%, #16213e 50%, #1a1a2e 100%)', textColor: '#ffffff', borderColor: '#52c41a' },
    { name: '紫色渐变', bgGradient: 'linear-gradient(135deg, #2d132c 0%, #5a2555 50%, #9b4f96 100%)', textColor: '#ffffff', borderColor: '#722ed1' },
    { name: '橙色渐变', bgGradient: 'linear-gradient(135deg, #2d132c 0%, #5a3d2b 50%, #9b6f4f 100%)', textColor: '#ffffff', borderColor: '#fa8c16' },
    { name: '青色渐变', bgGradient: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)', textColor: '#ffffff', borderColor: '#13c2c2' },
    { name: '粉色渐变', bgGradient: 'linear-gradient(135deg, #c94b4b 0%, #4b134f 100%)', textColor: '#ffffff', borderColor: '#eb2f96' },
    { name: '天蓝渐变', bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textColor: '#ffffff', borderColor: '#531dab' },
    { name: '暖色渐变', bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', textColor: '#ffffff', borderColor: '#eb2f96' },
    { name: '冷色渐变', bgGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', textColor: '#ffffff', borderColor: '#1890ff' }
  ];

  const availableModules = [
    { id: 'schedule', name: '课表', icon: '📅', size: 'large', color1: '#1890ff', color2: '#096dd9' },
    { id: 'reward', name: '奖卡', icon: '🏆', size: 'small', color1: '#52c41a', color2: '#389e0d' },
    { id: 'behavior', name: '行规', icon: '📋', size: 'small', color1: '#722ed1', color2: '#531dab' },
    { id: 'notification', name: '通知', icon: '🔔', size: 'medium', color1: '#fa8c16', color2: '#d46b08' },
    { id: 'honor', name: '荣誉', icon: '⭐', size: 'small', color1: '#eb2f96', color2: '#c41d7f' },
    { id: 'attendance', name: '考勤', icon: '✅', size: 'small', color1: '#13c2c2', color2: '#08979c' },
    { id: 'lunch', name: '午餐', icon: '🍱', size: 'medium', color1: '#2f54eb', color2: '#1d39c4' }
  ];

  useEffect(() => {
    fetchSchoolPageConfig();
  }, []);

  const fetchSchoolPageConfig = async () => {
    try {
      const response = await api.get(`/school/school/${teacher.school_id}/page-config`);
      let config = response.data;
      if (config.modules) {
        config.modules = config.modules.map(mod => {
          const defaultMod = availableModules.find(m => m.id === mod.id);
          return {
            ...mod,
            color1: mod.color1 || mod.color || defaultMod?.color1 || '#1890ff',
            color2: mod.color2 || defaultMod?.color2 || '#096dd9',
            gradientType: mod.gradientType || 'horizontal'
          };
        });
      }
      setSchoolPageConfig(config);
    } catch (error) {
      message.error('获取学校页面配置失败');
    }
  };

  const saveSchoolPageConfig = async () => {
    setLoading(true);
    try {
      await api.put(`/school/school/${teacher.school_id}/page-config`, schoolPageConfig);
      message.success('学校页面配置已保存');
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await api.get('/school/devices', {
        params: { type: DEVICE_TYPES.CLASS_BOARD }
      });
      setDevices(response.data);
      setSelectedDevices([]);
    } catch (error) {
      message.error('获取设备列表失败');
    }
  };

  const isDeviceOnline = (status) => (
    status === '在线' || status === 'online' || status === 1 || status === true
  );

  const openDeviceModal = () => {
    fetchDevices();
    setDeviceModalVisible(true);
  };

  const handleDeviceSelect = (deviceId, checked) => {
    if (checked) {
      setSelectedDevices([...selectedDevices, deviceId]);
    } else {
      setSelectedDevices(selectedDevices.filter(id => id !== deviceId));
    }
  };

  const handleSelectAllDevices = (checked) => {
    if (checked) {
      setSelectedDevices(devices.map(d => d.id));
    } else {
      setSelectedDevices([]);
    }
  };

  const distributeToSelectedDevices = async () => {
    if (selectedDevices.length === 0) {
      message.warning('请至少选择一台设备');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/school/school/${teacher.school_id}/page-config/distribute`, {
        device_ids: selectedDevices
      });
      message.success(`配置已分发到 ${selectedDevices.length} 台设备`);
      
      for (const deviceId of selectedDevices) {
        try {
          await api.post(`/school/devices/${deviceId}/control/power`, {
            action: 'restart'
          });
        } catch (restartError) {
          console.warn(`设备 ${deviceId} 重启指令发送失败:`, restartError);
        }
      }
      message.success('已向所有设备发送重启指令');
      
      setDeviceModalVisible(false);
      setSelectedDevices([]);
    } catch (error) {
      message.error('分发失败');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleReorder = (fromIdx, toIdx) => {
    const orderedModules = [...schoolPageConfig.modules].sort((a, b) => a.position - b.position);
    const visibleModules = orderedModules.filter(mod => mod.visible !== false);
    const fromModule = visibleModules[fromIdx];

    if (!fromModule) return;

    const nextVisibleModules = [...visibleModules];
    const [removed] = nextVisibleModules.splice(fromIdx, 1);
    const nextIndex = Math.max(0, Math.min(toIdx, nextVisibleModules.length));
    nextVisibleModules.splice(nextIndex, 0, removed);

    let visibleIndex = 0;
    const newModules = orderedModules.map(mod => (
      mod.visible === false ? mod : nextVisibleModules[visibleIndex++]
    ));

    newModules.forEach((m, i) => m.position = i);
    setSchoolPageConfig({ ...schoolPageConfig, modules: newModules });
  };

  const getPreviewDropIndex = (event, fromIdx, maxIndex) => {
    const moduleElements = Array.from(event.currentTarget.querySelectorAll('[data-preview-module-index]'))
      .map(element => ({
        element,
        index: Number(element.getAttribute('data-preview-module-index')),
        rect: element.getBoundingClientRect()
      }))
      .filter(item => item.index !== fromIdx);

    if (moduleElements.length === 0) {
      return 0;
    }

    const pointerX = event.clientX;
    const pointerY = event.clientY;
    const closest = moduleElements.reduce((best, item) => {
      const centerX = item.rect.left + item.rect.width / 2;
      const centerY = item.rect.top + item.rect.height / 2;
      const distance = ((pointerX - centerX) ** 2) + ((pointerY - centerY) ** 2);
      return distance < best.distance ? { ...item, distance } : best;
    }, { distance: Infinity });

    const closestCenterY = closest.rect.top + closest.rect.height / 2;
    const isAfterClosest = pointerY > closestCenterY;

    return Math.max(0, Math.min(maxIndex, closest.index + (isAfterClosest ? 1 : 0)));
  };

  const handleModuleColorChange = (modId, colorType, value) => {
    const newModules = schoolPageConfig.modules.map(m =>
      m.id === modId ? { ...m, [colorType]: value } : m
    );
    setSchoolPageConfig({ ...schoolPageConfig, modules: newModules });
  };

  const handleGradientTypeChange = (modId, value) => {
    const newModules = schoolPageConfig.modules.map(m =>
      m.id === modId ? { ...m, gradientType: value } : m
    );
    setSchoolPageConfig({ ...schoolPageConfig, modules: newModules });
  };

  const handleModuleVisibilityChange = (modId, visible) => {
    const newModules = schoolPageConfig.modules.map(m =>
      m.id === modId ? { ...m, visible } : m
    );
    setSchoolPageConfig({ ...schoolPageConfig, modules: newModules });
  };

  const handleThemeChange = (theme) => {
    setSchoolPageConfig({
      ...schoolPageConfig,
      bg_gradient: theme.bgGradient,
      text_color: theme.textColor,
      border_color: theme.borderColor
    });
  };

  const getModuleGradient = (mod) => {
    const color1 = mod.color1 || '#1890ff';
    const color2 = mod.color2 || '#096dd9';
    const gradientType = mod.gradientType || 'horizontal';
    
    if (gradientType === 'vertical') {
      return `linear-gradient(180deg, ${color1} 0%, ${color2} 100%)`;
    } else if (gradientType === 'radial') {
      return `radial-gradient(circle, ${color1} 0%, ${color2} 100%)`;
    } else {
      return `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`;
    }
  };

  const generateSimilarColors = (baseColor) => {
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const lighten = (color, percent) => {
      const num = parseInt(color, 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.min(255, (num >> 16) + amt);
      const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
      const B = Math.min(255, (num & 0x0000FF) + amt);
      return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
    };
    
    const darken = (color, percent) => {
      const num = parseInt(color, 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.max(0, (num >> 16) - amt);
      const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
      const B = Math.max(0, (num & 0x0000FF) - amt);
      return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
    };
    
    const hexStr = `${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    return {
      color1: lighten(hexStr, 20),
      color2: darken(hexStr, 20)
    };
  };

  const presetColors = [
    '#1890ff', '#52c41a', '#722ed1', '#fa8c16', 
    '#eb2f96', '#13c2c2', '#2f54eb', '#f5576c'
  ];

  const resetModules = async () => {
    Modal.confirm({
      title: '确认重置',
      content: '确定要重置模块配置吗？将从学校班级主题应用重新加载数据。',
      onOk: async () => {
        try {
          const response = await api.get('/school/class-apps');
          const classApps = response.data.apps || [];
          
          const themeApps = classApps.filter(app => app.app_subtype === 'theme' && app.is_active);
          
          const newModules = themeApps.map((app, index) => {
              const baseColor = presetColors[index % presetColors.length];
              const colors = generateSimilarColors(baseColor);
              return {
                id: app.app_key,
                name: app.app_name,
                size: app.size || 'medium',
                position: app.sort_order || index,
                color1: colors.color1,
                color2: colors.color2,
                gradientType: 'horizontal',
                visible: true
              };
            });
          
          setSchoolPageConfig(prev => ({
            ...prev,
            modules: newModules
          }));
          message.success('模块配置已重置');
        } catch (error) {
          message.error('重置失败');
        }
      }
    });
  };

  if (!schoolPageConfig) {
    return (
      <div style={{ padding: '24px' }}>
        <Card title="自定义模块配置">
          <p>加载中...</p>
        </Card>
      </div>
    );
  }

  const sortedModules = [...schoolPageConfig.modules].sort((a, b) => a.position - b.position);

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title="自定义模块配置" 
        extra={
          <div style={{ display: 'flex', gap: 12 }}>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={saveSchoolPageConfig}
              loading={loading}
            >
              保存配置
            </Button>
            <Button 
              type="danger" 
              icon={<SendOutlined />} 
              onClick={openDeviceModal}
              loading={loading}
            >
              发到所有设备
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', gap: 24, minHeight: 600 }}>
          {/* 左侧配置面板 - 隐藏但保留功能 */}
          <div style={{ width: 320, flex: 'none', display: 'none' }}>
            <Form layout="vertical">
              <Form.Item label="风格选择">
                <Select
                  value={schoolPageConfig.style}
                  onChange={(style) => setSchoolPageConfig(prev => ({ ...prev, style }))}
                  style={{ width: '100%' }}
                  options={styleOptions}
                />
              </Form.Item>

              <Form.Item label="主题风格（含渐变背景）">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {themeColors.map((color, index) => (
                    <button
                      key={index}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        background: color.bgGradient,
                        border: schoolPageConfig.bg_gradient === color.bgGradient ? '3px solid #000' : '1px solid #ccc',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleThemeChange(color)}
                      title={color.name}
                    />
                  ))}
                </div>
              </Form.Item>

              <Form.Item label="文字颜色">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="color"
                    value={schoolPageConfig.text_color || '#ffffff'}
                    onChange={(e) => setSchoolPageConfig({ ...schoolPageConfig, text_color: e.target.value })}
                    style={{ width: 50, height: 50, borderRadius: 8, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14 }}>{schoolPageConfig.text_color || '#ffffff'}</span>
                </div>
              </Form.Item>

              <Form.Item label="边框颜色">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="color"
                    value={schoolPageConfig.border_color || '#333333'}
                    onChange={(e) => setSchoolPageConfig({ ...schoolPageConfig, border_color: e.target.value })}
                    style={{ width: 50, height: 50, borderRadius: 8, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14 }}>{schoolPageConfig.border_color || '#333333'}</span>
                </div>
              </Form.Item>

              <Form.Item label="背景图片URL">
                <input
                  type="text"
                  value={schoolPageConfig.background_image || ''}
                  onChange={(e) => setSchoolPageConfig({ ...schoolPageConfig, background_image: e.target.value })}
                  placeholder="输入背景图片URL（可选）"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 4, border: '1px solid #d9d9d9' }}
                />
              </Form.Item>
            </Form>
          </div>

          {/* 右侧：模块排列与颜色 + Pad预览区域 */}
          <div style={{ flex: 1, display: 'flex', gap: 24 }}>
            {/* 模块排列与颜色 */}
            <div style={{ width: 400, flex: 'none' }}>
              <Form layout="vertical">
                <Form.Item label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>模块排列与颜色</span>
                    <Button
                      type="default"
                      icon={<ReloadOutlined />}
                      onClick={resetModules}
                      size="small"
                    >
                      重置
                    </Button>
                  </div>
                }>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, maxHeight: 500, overflowY: 'auto' }}>
                    {schoolPageConfig.modules.map((mod, idx) => (
                      <div
                        key={mod.id}
                        style={{
                          background: getModuleGradient(mod),
                          padding: '10px',
                          borderRadius: 8,
                          color: 'white'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ flex: 1, fontWeight: '500', fontSize: '13px' }}>{mod.name}</span>
                          <span style={{ fontSize: '11px', opacity: 0.8 }}>
                            {mod.size === 'large' ? '大' : mod.size === 'medium' ? '中' : '小'}
                          </span>
                        </div>
                        {/* 颜色和渐变方式 - 隐藏但保留功能 */}
                        <div style={{ display: 'none' }}>
                          <span style={{ fontSize: '10px', opacity: 0.8 }}>颜色:</span>
                          <input
                            type="color"
                            value={mod.color1 || '#1890ff'}
                            onChange={(e) => handleModuleColorChange(mod.id, 'color1', e.target.value)}
                            style={{ width: 28, height: 28, borderRadius: 4, cursor: 'pointer', border: 'none' }}
                          />
                          <input
                            type="color"
                            value={mod.color2 || '#096dd9'}
                            onChange={(e) => handleModuleColorChange(mod.id, 'color2', e.target.value)}
                            style={{ width: 28, height: 28, borderRadius: 4, cursor: 'pointer', border: 'none' }}
                          />
                          <Select
                            value={mod.gradientType || 'horizontal'}
                            onChange={(type) => handleGradientTypeChange(mod.id, type)}
                            style={{ width: 60, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '10px' }}
                            options={gradientTypeOptions}
                            size="small"
                          />
                        </div>
                        {/* 显示开关 */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                          <span style={{ fontSize: '10px', opacity: 0.8 }}>显示:</span>
                          <Switch
                            checked={mod.visible !== false}
                            onChange={(checked) => handleModuleVisibilityChange(mod.id, checked)}
                            size="small"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Form.Item>
              </Form>
            </div>

            {/* Pad竖屏预览 */}
            <div style={{ flex: 1 }}>
              <Form layout="vertical">
                <Form.Item label="Pad竖屏预览（21.5寸模拟）">
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    {(() => {
                      const gridStyle = {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(10, 1fr)',
                        gridTemplateRows: 'repeat(4, 1fr)',
                        gap: '12px',
                        background: schoolPageConfig.bg_gradient || themeColors[0].bgGradient,
                        backgroundSize: 'cover',
                        padding: '16px',
                        borderRadius: '16px',
                        width: 'min(100%, 360px)',
                        aspectRatio: '4 / 6',
                        boxShadow: '0 0 20px rgba(0,0,0,0.3)'
                      };

                      const getModuleStyle = (mod) => {
                        let gridColumn = 'span 4';
                        let gridRow = 'span 1';
                        
                        if (mod.size === 'large') {
                          gridColumn = 'span 6';
                          gridRow = 'span 2';
                        } else if (mod.size === 'medium') {
                          gridColumn = 'span 6';
                          gridRow = 'span 1';
                        } else {
                          gridColumn = 'span 4';
                          gridRow = 'span 1';
                        }
                        
                        return {
                          gridColumn,
                          gridRow,
                          background: getModuleGradient(mod),
                          borderRadius: '12px',
                          padding: '12px',
                          color: schoolPageConfig.text_color || '#ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          border: `1px solid ${schoolPageConfig.border_color || '#333'}`
                        };
                      };

                      const visibleModules = sortedModules.filter(mod => mod.visible !== false);
                      
                      return (
                        <div
                          style={gridStyle}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIdx = parseInt(e.dataTransfer.getData('targetIdx'));
                            if (Number.isNaN(fromIdx)) return;
                            const targetIdx = getPreviewDropIndex(e, fromIdx, visibleModules.length);
                            handleModuleReorder(fromIdx, targetIdx);
                          }}
                        >
                          {visibleModules.map((mod, idx) => (
                            <div 
                              key={mod.id} 
                              data-preview-module-index={idx}
                              style={{
                                ...getModuleStyle(mod),
                                cursor: 'move',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                              }}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('targetIdx', idx);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const fromIdx = parseInt(e.dataTransfer.getData('targetIdx'));
                                if (Number.isNaN(fromIdx)) return;
                                handleModuleReorder(fromIdx, idx);
                              }}
                              onDragOver={(e) => e.preventDefault()}
                              onDragEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                              onDragLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              onDragEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <span style={{ fontSize: mod.size === 'large' ? '36px' : mod.size === 'medium' ? '24px' : '18px' }}>{mod.icon}</span>
                              <span style={{ marginTop: '8px', fontSize: '14px', fontWeight: '500' }}>{mod.name}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </Card>

      {/* 设备选择模态框 */}
      <Modal
        title="选择设备"
        open={deviceModalVisible}
        onCancel={() => {
          setDeviceModalVisible(false);
          setSelectedDevices([]);
        }}
        okText="确定分发"
        cancelText="取消"
        confirmLoading={loading}
        onOk={distributeToSelectedDevices}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={selectedDevices.length === devices.length && devices.length > 0}
            onChange={(e) => handleSelectAllDevices(e.target.checked)}
          >
            全选 ({devices.length} 台设备)
          </Checkbox>
        </div>
        <Table
          columns={[
            {
              title: (
                <Checkbox
                  checked={selectedDevices.length === devices.length && devices.length > 0}
                  onChange={(e) => handleSelectAllDevices(e.target.checked)}
                />
              ),
              render: (_, record) => (
                <Checkbox
                  checked={selectedDevices.includes(record.id)}
                  onChange={(e) => handleDeviceSelect(record.id, e.target.checked)}
                />
              ),
              width: 60
            },
            { title: '设备名称', dataIndex: 'name', key: 'name' },
            { title: '设备编号', dataIndex: 'device_id', key: 'device_id' },
            { title: '班级', dataIndex: 'class_name', key: 'class_name' },
            { title: '状态', dataIndex: 'status', key: 'status', render: (status) => (
              <span style={{ color: isDeviceOnline(status) ? '#52c41a' : '#ff4d4f' }}>
                {isDeviceOnline(status) ? '在线' : '离线'}
              </span>
            )}
          ]}
          dataSource={devices}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          bordered
        />
        <div style={{ marginTop: 16, color: '#666', fontSize: 14 }}>
          已选择 {selectedDevices.length} 台设备
        </div>
      </Modal>
    </div>
  );
};

export default SchoolPageConfigPage;
